// Deterministic tag-chip coloring shared by:
//   - Source tag chips (TagsEditor / SettingsCardsV2)
//   - Schema-group tag chips (FingerprintGroupsV2)
//   - Event-table input.tags column (V4 events table)
//
// Color is hashed from `key:value` so every distinct (key, value) pair
// gets its own slot in the palette. E.g. `entity=person` is one color,
// `entity=deal` is another. Per-key uniformity moved out 2026-05-12
// per operator request — operators need to distinguish entity types at
// a glance, not just "tag families".
//
// 24-slot palette (was 12 — doubled to reduce visual collisions now
// that the hash space is wider).

const PALETTE = [
  { bg: 'oklch(0.96 0.05 15 / 0.7)',  fg: 'oklch(0.45 0.20 15)',  border: 'oklch(0.85 0.12 15)' },
  { bg: 'oklch(0.96 0.05 45 / 0.7)',  fg: 'oklch(0.45 0.18 45)',  border: 'oklch(0.85 0.12 45)' },
  { bg: 'oklch(0.96 0.05 75 / 0.7)',  fg: 'oklch(0.45 0.16 75)',  border: 'oklch(0.85 0.12 75)' },
  { bg: 'oklch(0.96 0.05 105 / 0.7)', fg: 'oklch(0.42 0.16 105)', border: 'oklch(0.82 0.12 105)' },
  { bg: 'oklch(0.96 0.05 135 / 0.7)', fg: 'oklch(0.42 0.16 135)', border: 'oklch(0.82 0.12 135)' },
  { bg: 'oklch(0.96 0.05 165 / 0.7)', fg: 'oklch(0.42 0.14 165)', border: 'oklch(0.82 0.12 165)' },
  { bg: 'oklch(0.96 0.05 195 / 0.7)', fg: 'oklch(0.42 0.14 195)', border: 'oklch(0.82 0.12 195)' },
  { bg: 'oklch(0.96 0.05 225 / 0.7)', fg: 'oklch(0.45 0.16 225)', border: 'oklch(0.85 0.12 225)' },
  { bg: 'oklch(0.96 0.05 255 / 0.7)', fg: 'oklch(0.45 0.18 255)', border: 'oklch(0.85 0.12 255)' },
  { bg: 'oklch(0.96 0.05 285 / 0.7)', fg: 'oklch(0.48 0.18 285)', border: 'oklch(0.85 0.12 285)' },
  { bg: 'oklch(0.96 0.05 315 / 0.7)', fg: 'oklch(0.48 0.20 315)', border: 'oklch(0.85 0.12 315)' },
  { bg: 'oklch(0.96 0.05 345 / 0.7)', fg: 'oklch(0.48 0.20 345)', border: 'oklch(0.85 0.12 345)' },
  { bg: 'oklch(0.93 0.07 30 / 0.7)',  fg: 'oklch(0.42 0.20 30)',  border: 'oklch(0.82 0.14 30)' },
  { bg: 'oklch(0.93 0.07 60 / 0.7)',  fg: 'oklch(0.42 0.18 60)',  border: 'oklch(0.82 0.14 60)' },
  { bg: 'oklch(0.93 0.07 90 / 0.7)',  fg: 'oklch(0.40 0.16 90)',  border: 'oklch(0.80 0.14 90)' },
  { bg: 'oklch(0.93 0.07 120 / 0.7)', fg: 'oklch(0.40 0.16 120)', border: 'oklch(0.80 0.14 120)' },
  { bg: 'oklch(0.93 0.07 150 / 0.7)', fg: 'oklch(0.40 0.16 150)', border: 'oklch(0.80 0.14 150)' },
  { bg: 'oklch(0.93 0.07 180 / 0.7)', fg: 'oklch(0.40 0.14 180)', border: 'oklch(0.80 0.14 180)' },
  { bg: 'oklch(0.93 0.07 210 / 0.7)', fg: 'oklch(0.40 0.14 210)', border: 'oklch(0.80 0.14 210)' },
  { bg: 'oklch(0.93 0.07 240 / 0.7)', fg: 'oklch(0.42 0.16 240)', border: 'oklch(0.82 0.14 240)' },
  { bg: 'oklch(0.93 0.07 270 / 0.7)', fg: 'oklch(0.42 0.18 270)', border: 'oklch(0.82 0.14 270)' },
  { bg: 'oklch(0.93 0.07 300 / 0.7)', fg: 'oklch(0.45 0.18 300)', border: 'oklch(0.82 0.14 300)' },
  { bg: 'oklch(0.93 0.07 330 / 0.7)', fg: 'oklch(0.45 0.20 330)', border: 'oklch(0.82 0.14 330)' },
  { bg: 'oklch(0.93 0.07 0 / 0.7)',   fg: 'oklch(0.45 0.20 0)',   border: 'oklch(0.82 0.14 0)' },
] as const;

function hashTag(s: string): number {
  // 32-bit FNV-1a — better avalanche than the prior `h*31` for
  // similar-prefix strings like "entity=person" vs "entity=deal".
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** Return a palette slot for a (key, value) pair. Each distinct pair
 *  gets its own colour. */
export function colorForTag(key: string, value: string): typeof PALETTE[number] {
  return PALETTE[hashTag(`${key}:${value}`) % PALETTE.length]!;
}

/** Back-compat: color by key only. Equivalent to colorForTag(key, '').
 *  Kept so existing callers that pass only the key keep working until
 *  they're migrated to colorForTag(k, v). */
export function colorForTagKey(key: string): typeof PALETTE[number] {
  return colorForTag(key, '');
}

/** Render a tag chip styled per-key. Renders inline; caller controls
 *  flex container.
 *
 *  Props:
 *    - onClick: makes the chip a button (cursor:pointer + button-role
 *      semantics). Used by source-list rows to click-to-filter.
 *    - onRemove: shows an × suffix; used by the TagsEditor inline editor.
 *    - compact: smaller paddings + font, for dense list rows.
 *    - hideKey: omit the key prefix — shows only the value, useful
 *      where the row already groups by key (e.g. per-tag-key columns).
 */
export function TagChip({ k, v, onRemove, onClick, title, compact, hideKey }: {
  k: string;
  v: string;
  onRemove?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
  compact?: boolean;
  hideKey?: boolean;
}): React.ReactElement {
  const c = colorForTag(k, v);
  const pad = compact ? '1px 6px' : '2px 8px';
  const fontSize = compact ? 10.5 : 11;
  const radius = compact ? 3 : 4;
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: pad, fontSize, fontFamily: 'var(--mono)',
    background: c.bg, border: `1px solid ${c.border}`, color: c.fg,
    borderRadius: radius, maxWidth: '100%',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    cursor: onClick ? 'pointer' : 'default',
  };
  const content = (
    <>
      {!hideKey && <>
        <span style={{ fontWeight: 600 }}>{k}</span>
        <span aria-hidden="true" style={{ opacity: 0.5 }}>=</span>
      </>}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          aria-label={`Remove ${k}`}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'currentColor', opacity: 0.7, padding: 0, marginLeft: 2 }}
        >×</button>
      )}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title ?? `${k}=${v}`}
        style={baseStyle}
      >{content}</button>
    );
  }
  return (
    <span title={title ?? `${k}=${v}`} style={baseStyle}>{content}</span>
  );
}
